import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

import { fetchResellers } from '../duck'
import './style.scss'

const MIN_ZOOM = 4
const MAX_ZOOM = 9
const TILE_URL = 'https://maptiles.flybringit.se/positron/v1/{z}/{x}/{y}{r}.webp'
const TILE_BOUNDS = [[50, -25], [71.5, 32]]
const TILE_ATTRIBUTION =
  '<a href="https://openmaptiles.org/" target="_blank" rel="noopener noreferrer">© OpenMapTiles</a> ' +
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>'

const MARKER_ICON = L.divIcon({
  className: 'rm-pin',
  html: '<div class="rm-pin-shape"></div>',
  iconSize: [22, 36],
  iconAnchor: [11, 36],
})

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const buildPopupHtml = (city, names) => `<div class="rm-popup">
  <div class="rm-popup-header">${escapeHtml(city)}</div>
  <div class="rm-popup-body">
    ${names.map((n) => `<div class="rm-popup-name">${escapeHtml(n)}</div>`).join('')}
  </div>
</div>`

const ResellerMap = ({ history }) => {
  const dispatch = useDispatch()
  const items = useSelector((state) => state.resellers.items)
  const lng = useSelector((state) => state.app.language)

  const mapEl = useRef(null)
  const mapRef = useRef(null)
  const clusterRef = useRef(null)
  const blurTimerRef = useRef(null)

  const [search, setSearch] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [dropdownIndex, setDropdownIndex] = useState(-1)

  useEffect(() => {
    if (!items) {
      dispatch(fetchResellers())
    }
  }, [dispatch, items])

  useEffect(() => () => clearTimeout(blurTimerRef.current), [])

  const allMarkers = useMemo(() => {
    if (!items) return []
    const out = []
    items.forEach((country) => {
      country.Cities?.forEach((city) => {
        ;(city.Customers || []).forEach((customer) => {
          if (customer.Latitude == null || customer.Longitude == null) return
          out.push({
            name: customer.Name,
            lat: customer.Latitude,
            lng: customer.Longitude,
            city: city.City,
          })
        })
      })
    })
    return out
  }, [items])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allMarkers
    return allMarkers.filter(
      (m) =>
        m.city.toLowerCase().includes(q) ||
        m.name.toLowerCase().includes(q)
    )
  }, [allMarkers, search])

  useEffect(() => {
    if (mapRef.current) return
    const map = L.map(mapEl.current, {
      center: [62, 17],
      zoom: 5,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      maxBounds: TILE_BOUNDS,
      maxBoundsViscosity: 1,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
    })
    L.control.attribution({ position: 'bottomleft', prefix: false }).addTo(map)
    L.tileLayer(TILE_URL, {
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      bounds: TILE_BOUNDS,
      attribution: TILE_ATTRIBUTION,
    }).addTo(map)

    clusterRef.current = L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      showCoverageOnHover: false,
      disableClusteringAtZoom: 10,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount()
        return L.divIcon({
          html: `<div class="rm-pin-shape rm-cluster"><span>${count}</span></div>`,
          className: 'rm-pin',
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        })
      },
    })

    clusterRef.current.on('clusterclick', (e) => {
      const markers = e.layer.getAllChildMarkers()
      const businesses = markers.map((m) => m.options.resellerData)

      if (businesses.length === 1) {
        markers[0].openPopup()
        return
      }

      if (map.getZoom() >= MAX_ZOOM) {
        L.popup({ className: 'rm-popup-container', closeButton: false })
          .setLatLng(e.latlng)
          .setContent(buildPopupHtml(businesses[0].city, businesses.map((b) => b.name)))
          .addTo(map)
        return
      }

      map.flyToBounds(e.layer.getBounds().pad(0.5), { duration: 0.5, maxZoom: MAX_ZOOM })
    })
    map.addLayer(clusterRef.current)

    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
      clusterRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const cluster = clusterRef.current
    if (!map || !cluster) return
    cluster.clearLayers()

    const markers = filtered.map((m) => {
      const marker = L.marker([m.lat, m.lng], {
        icon: MARKER_ICON,
        resellerData: m,
        interactive: true,
      })
      marker.bindPopup(buildPopupHtml(m.city, [m.name]), {
        className: 'rm-popup-container',
        closeButton: false,
        offset: [0, -30],
      })
      return marker
    })
    cluster.addLayers(markers)
  }, [filtered])

  const t = (key, fallback) => lng?.Reseller?.[key] ?? fallback

  const onApply = () => {
    history.push('/application')
  }

  const handleSearchSelect = (result) => {
    const map = mapRef.current
    if (!map) return
    map.flyTo([result.lat, result.lng], MAX_ZOOM, { duration: 0.5 })
    setSearch('')
    setShowDropdown(false)
    setDropdownIndex(-1)
  }

  const handleKeyDown = (e) => {
    if (!showDropdown || filtered.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setDropdownIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setDropdownIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && dropdownIndex >= 0) {
      e.preventDefault()
      handleSearchSelect(filtered[dropdownIndex])
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
      setDropdownIndex(-1)
    }
  }

  return (
    <div className="resellerMap">
      <div className="resellerMap__title">
        <h2>{t('MapTitle', 'Våra återförsäljare')}</h2>
      </div>

      <button type="button" className="resellerMap__cta-mobile" onClick={onApply}>
        {t('BecomeReseller', 'Bli återförsäljare')}
      </button>

      <div className="resellerMap__map" ref={mapEl} />

      <div className="resellerMap__top-right">
        <button type="button" className="resellerMap__cta" onClick={onApply}>
          {t('BecomeReseller', 'Bli återförsäljare')}
        </button>
        <div className="resellerMap__search">
          <input
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showDropdown && filtered.length > 0}
            aria-haspopup="listbox"
            aria-controls="resellerMap-search-listbox"
            aria-activedescendant={
              dropdownIndex >= 0 ? `resellerMap-search-option-${dropdownIndex}` : undefined
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setShowDropdown(e.target.value.length > 0)
              setDropdownIndex(-1)
            }}
            onFocus={() => search.length > 0 && setShowDropdown(true)}
            onBlur={() => {
              blurTimerRef.current = setTimeout(() => setShowDropdown(false), 200)
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('SearchPlaceholder', 'Sök ort eller butik')}
          />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          {showDropdown && filtered.length > 0 && (
            <div
              id="resellerMap-search-listbox"
              role="listbox"
              className="resellerMap__search-dropdown"
            >
              {filtered.slice(0, 8).map((r, i) => (
                <div
                  key={`${r.city}-${r.name}`}
                  id={`resellerMap-search-option-${i}`}
                  role="option"
                  aria-selected={i === dropdownIndex}
                  className={`resellerMap__search-result ${i === dropdownIndex ? 'active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSearchSelect(r)}
                >
                  <div className="resellerMap__search-result-name">{r.name}</div>
                  <div className="resellerMap__search-result-city">{r.city}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="resellerMap__zoom">
        <button type="button" onClick={() => mapRef.current?.zoomIn()} aria-label="Zooma in">
          +
        </button>
        <button type="button" onClick={() => mapRef.current?.zoomOut()} aria-label="Zooma ut">
          −
        </button>
      </div>
    </div>
  )
}

export default withRouter(ResellerMap)